import * as XLSX from 'xlsx';

export const TYRE_IMPORT_COLUMNS = [
  { key: 'site', label: 'Inventory site', aliases: ['site', 'site id', 'site name'] },
  { key: 'serial_number', label: 'Manufacturer serial', required: true, aliases: ['serial', 'serial number', 'manufacturer serial number'] },
  { key: 'tyre_id', label: 'Tyre ID', aliases: ['tyre number', 'tire id', 'tire number'] },
  { key: 'brand', label: 'Brand', required: true, aliases: ['manufacturer', 'make'] },
  { key: 'model', label: 'Model', required: true },
  { key: 'size', label: 'Size', required: true, aliases: ['tyre size', 'tire size'] },
  { key: 'tyre_type', label: 'Tyre type', required: true, aliases: ['type', 'tire type'] },
  { key: 'supplier', label: 'Supplier', required: true, aliases: ['vendor'] },
  { key: 'manufacturing_date', label: 'Manufacturing date', required: true, type: 'date', aliases: ['manufactured date', 'manufacture date'] },
  { key: 'purchase_date', label: 'Purchase date', required: true, type: 'date' },
  { key: 'purchase_cost', label: 'Purchase cost', type: 'number', aliases: ['cost', 'unit cost', 'price'] },
  { key: 'initial_tread_depth_mm', label: 'Initial tread depth (mm)', required: true, type: 'number', aliases: ['initial tread depth', 'tread depth', 'tread depth mm'] },
  { key: 'warranty', label: 'Warranty', required: true },
  { key: 'batch_number', label: 'Batch number', aliases: ['batch'] },
  { key: 'invoice_number', label: 'Invoice number', aliases: ['invoice', 'invoice no', 'invoice #'] },
  { key: 'remarks', label: 'Remarks', aliases: ['notes', 'comments'] },
];

const normaliseHeader = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const columnByHeader = TYRE_IMPORT_COLUMNS.reduce((lookup, column) => {
  [column.label, column.key, ...(column.aliases || [])].forEach((header) => {
    lookup[normaliseHeader(header)] = column;
  });
  return lookup;
}, {});

const pad = (value) => String(value).padStart(2, '0');

const formatDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) return `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`;
  }
  const text = String(value || '').trim();
  if (!text) return '';
  const isoDate = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoDate) return `${isoDate[1]}-${pad(isoDate[2])}-${pad(isoDate[3])}`;
  const localDate = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (localDate) return `${localDate[3]}-${pad(localDate[2])}-${pad(localDate[1])}`;
  return text;
};

const isBlankRow = (row) => !row.some((value) => String(value ?? '').trim());

export const parseTyreWorkbook = (buffer) => {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  const worksheet = workbook.Sheets.Tyres || workbook.Sheets[workbook.SheetNames[0]];
  if (!worksheet) throw new Error('The workbook does not contain a worksheet.');

  const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: true });
  if (!data.length || isBlankRow(data[0])) throw new Error('The first row must contain the column headings.');

  const mappedColumns = data[0].map((header) => columnByHeader[normaliseHeader(header)] || null);
  const recognised = mappedColumns.filter(Boolean);
  if (!recognised.length) throw new Error('No recognised tyre columns were found. Download the template and keep its column headings.');

  const duplicateKeys = recognised
    .map((column) => column.key)
    .filter((key, index, keys) => keys.indexOf(key) !== index);
  if (duplicateKeys.length) throw new Error('The worksheet contains duplicate tyre columns.');

  return data.slice(1).reduce((rows, cells, index) => {
    if (isBlankRow(cells)) return rows;
    const values = mappedColumns.reduce((row, column, columnIndex) => {
      if (!column) return row;
      const rawValue = cells[columnIndex];
      if (column.type === 'date') row[column.key] = formatDate(rawValue);
      else row[column.key] = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
      return row;
    }, {});
    rows.push({ rowNumber: index + 2, values });
    return rows;
  }, []);
};

const validTyreTypes = new Set([
  'Radial',
  'Radial (Tubeless)',
  'Radial (Tube-type)',
  'Bias / Cross-ply',
  'Solid / Industrial',
]);

export const validateTyreRows = (rows, sites) => {
  const siteLookup = sites.reduce((lookup, site) => {
    lookup[String(site.id).toLowerCase()] = String(site.id);
    lookup[String(site.name || '').trim().toLowerCase()] = String(site.id);
    return lookup;
  }, {});
  const singleSite = sites.length === 1 ? String(sites[0].id) : '';
  const seenSerials = new Set();
  const seenTyreIds = new Set();

  return rows.map((row) => {
    const values = { ...row.values };
    const errors = [];
    TYRE_IMPORT_COLUMNS.filter((column) => column.required).forEach((column) => {
      if (values[column.key] === '' || values[column.key] === null || values[column.key] === undefined) {
        errors.push(`${column.label} is required`);
      }
    });

    const requestedSite = String(values.site || '').trim().toLowerCase();
    const resolvedSite = requestedSite ? siteLookup[requestedSite] : singleSite;
    if (!resolvedSite) errors.push(sites.length ? 'Inventory site is required or not recognised' : 'No inventory sites are available');
    else values.site = resolvedSite;

    if (values.tyre_type && !validTyreTypes.has(String(values.tyre_type).trim())) {
      errors.push('Tyre type is not recognised');
    }
    ['purchase_cost', 'initial_tread_depth_mm'].forEach((key) => {
      if (values[key] !== '' && values[key] !== undefined && (Number.isNaN(Number(values[key])) || Number(values[key]) < 0)) {
        errors.push(`${TYRE_IMPORT_COLUMNS.find((column) => column.key === key).label} must be zero or greater`);
      }
    });
    ['manufacturing_date', 'purchase_date'].forEach((key) => {
      if (values[key] && !/^\d{4}-\d{2}-\d{2}$/.test(values[key])) {
        errors.push(`${TYRE_IMPORT_COLUMNS.find((column) => column.key === key).label} must use YYYY-MM-DD or DD/MM/YYYY`);
      }
    });

    const serialKey = `${String(values.brand || '').toLowerCase()}::${String(values.serial_number || '').toLowerCase()}`;
    if (values.serial_number && seenSerials.has(serialKey)) errors.push('Manufacturer serial and brand are duplicated in this file');
    seenSerials.add(serialKey);
    const tyreIdKey = String(values.tyre_id || '').toLowerCase();
    if (tyreIdKey && seenTyreIds.has(tyreIdKey)) errors.push('Tyre ID is duplicated in this file');
    if (tyreIdKey) seenTyreIds.add(tyreIdKey);

    const payload = Object.entries(values).reduce((result, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) result[key] = value;
      return result;
    }, {});
    return { ...row, values, payload, errors, status: errors.length ? 'invalid' : 'ready' };
  });
};

export const downloadTyreImportTemplate = () => {
  const workbook = XLSX.utils.book_new();
  const headings = TYRE_IMPORT_COLUMNS.map((column) => column.label);
  const tyreSheet = XLSX.utils.aoa_to_sheet([headings]);
  tyreSheet['!cols'] = TYRE_IMPORT_COLUMNS.map((column) => ({ wch: Math.max(column.label.length + 3, 16) }));
  tyreSheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headings.length - 1)}1` };
  XLSX.utils.book_append_sheet(workbook, tyreSheet, 'Tyres');

  const instructions = [
    ['Tyre inventory import'],
    ['Enter one tyre per row in the Tyres sheet. Do not rename the column headings.'],
    ['Required fields', TYRE_IMPORT_COLUMNS.filter((column) => column.required).map((column) => column.label).join(', ')],
    ['Inventory site', 'Required when your account can access more than one site. Enter the site name or ID.'],
    ['Tyre ID', 'Optional. Leave blank to let the system generate it.'],
    ['Dates', 'Use YYYY-MM-DD or DD/MM/YYYY.'],
    ['Tyre type', Array.from(validTyreTypes).join(', ')],
    ['After import', 'New tyres start as Available at the selected inventory site.'],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet['!cols'] = [{ wch: 22 }, { wch: 110 }];
  XLSX.utils.book_append_sheet(workbook, instructionSheet, 'Instructions');
  XLSX.writeFile(workbook, 'tyre-inventory-import-template.xlsx');
};
